import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { TREND_COLORS } from '../../trendTheme';
import { useTrendAdmin } from '../TrendAdminContext';
import { sectionTitle } from '../adminUi';
import {
  formatShortDate,
  getSourceHealthColor,
  getSourceHealthLabel,
} from '../adminHelpers';

const HEALTH_ACCENT: Record<'default' | 'success' | 'warning' | 'error', string> = {
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  default: TREND_COLORS.divider,
};

export default function SourcesTab() {
  const {
    activeSourceCountLabel,
    openCreateDialog,
    sourceTestSourceIds,
    setSourceTestSourceIds,
    busyAction,
    hasActiveRun,
    handleStartSourceTest,
    sources,
    toggleSourceTestSource,
    openEditDialog,
    handleDeleteSource,
    handleImportRecommended,
    recommendedError,
    recommendedSources,
  } = useTrendAdmin();

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          {sectionTitle('Aktiva källor', 'Dessa källor används i trendkörningarna. Vikt styr prioritet.')}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>{activeSourceCountLabel}</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={openCreateDialog}>Ny källa</Button>
          </Stack>
        </Stack>
        <Stack spacing={1.5}>
          <Alert severity="info">
            Välj en eller flera importerade källor och kör ett källtest. Då hämtar backend källposter från just dessa källor och kör testanalys utan att publicera.
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Button
              size="small"
              variant="contained"
              disabled={sourceTestSourceIds.length === 0 || busyAction === 'pilotStart' || hasActiveRun}
              onClick={() => void handleStartSourceTest(sourceTestSourceIds)}
              sx={{ bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight } }}
            >
              Kör källtest för valda ({sourceTestSourceIds.length})
            </Button>
            {sourceTestSourceIds.length > 0 && (
              <Button size="small" variant="text" onClick={() => setSourceTestSourceIds([])}>
                Rensa val
              </Button>
            )}
          </Stack>
          {sources.map((source) => {
            const healthColor = getSourceHealthColor(source);
            const selected = sourceTestSourceIds.includes(source._id);
            return (
              <Box
                key={source._id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: TREND_COLORS.sectionBg,
                  border: `1px solid ${TREND_COLORS.divider}`,
                  borderLeft: `3px solid ${HEALTH_ACCENT[healthColor]}`,
                  opacity: source.active ? 1 : 0.6,
                  transition: 'box-shadow 0.15s ease',
                  '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.06)' },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Tooltip title={source.active ? 'Markera för källtest' : 'Inaktiv källa kan inte testas'}>
                    <span>
                      <Checkbox
                        size="small"
                        checked={selected}
                        disabled={!source.active}
                        onChange={() => toggleSourceTestSource(source._id)}
                        sx={{ p: 0.5, mt: -0.25, color: TREND_COLORS.textMuted, '&.Mui-checked': { color: TREND_COLORS.navy } }}
                      />
                    </span>
                  </Tooltip>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>{source.name}</Typography>
                          {!source.active && <Chip size="small" variant="outlined" label="Inaktiv" sx={{ height: 20 }} />}
                        </Stack>
                        <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                          {source.type} · {source.category} · vikt {source.weight}{source.sourceTier ? ` · Tier ${source.sourceTier}` : ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!source.active || busyAction === 'pilotStart' || hasActiveRun}
                          onClick={() => void handleStartSourceTest([source._id])}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Källtest
                        </Button>
                        <Tooltip title="Ändra källa">
                          <IconButton size="small" onClick={() => openEditDialog(source)} sx={{ color: TREND_COLORS.textSecondary }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ta bort källa">
                          <IconButton size="small" color="error" onClick={() => void handleDeleteSource(source._id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        color={healthColor}
                        label={`${getSourceHealthLabel(source)}${source.sourceHealthScore === null || source.sourceHealthScore === undefined ? '' : ` ${Math.round(source.sourceHealthScore)}/100`}`}
                      />
                      {source.historicalRelevanceScore !== null && source.historicalRelevanceScore !== undefined && (
                        <Chip size="small" variant="outlined" label={`Relevans ${Math.round(source.historicalRelevanceScore)}/100`} />
                      )}
                      {source.geography && source.geography !== 'unknown' && <Chip size="small" variant="outlined" label={source.geography} />}
                      {source.accessType && source.accessType !== 'unknown' && <Chip size="small" variant="outlined" label={source.accessType} />}
                      {source.lastSuccessfulFetchAt && (
                        <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>testad {formatShortDate(source.lastSuccessfulFetchAt)}</Typography>
                      )}
                      {source.url && (
                        <Link
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          variant="caption"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: TREND_COLORS.navy }}
                        >
                          Öppna källa <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                        </Link>
                      )}
                    </Stack>

                    {source.lastFailureReason && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.5 }}>
                        Senaste fel: {source.lastFailureReason}
                      </Typography>
                    )}

                    {source.focusTags.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        {source.focusTags.map((tag) => (
                          <Chip key={`${source._id}-${tag}`} size="small" label={tag} variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        ))}
                      </Stack>
                    )}

                    {source.targetedDiscovery?.enabled && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        <Chip
                          size="small"
                          color="warning"
                          label={`Riktad crawl: ${source.targetedDiscovery.crawlMode === 'category_pages' ? 'Kategorisidor' : 'Sitemap'}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Max ${source.targetedDiscovery.maxUrlsPerRun} URL/körning`}
                        />
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          {sectionTitle('Rekommenderade källor', 'Kuraterad startlista. Importera enstaka eller alla på en gång.')}
          <Button size="small" disabled={busyAction === 'import'} onClick={() => void handleImportRecommended()}>Importera alla</Button>
        </Stack>
        {recommendedError && <Alert severity="warning" sx={{ mb: 2 }}>{recommendedError}</Alert>}
        {!recommendedError && recommendedSources.length === 0 && (
          <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, fontStyle: 'italic' }}>Inga rekommenderade källor att visa.</Typography>
        )}
        <Stack spacing={1.5}>
          {recommendedSources.map((source) => (
            <Box
              key={source.key}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: TREND_COLORS.sectionBg,
                border: `1px solid ${TREND_COLORS.divider}`,
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{source.name}</Typography>
                  <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                    {source.group} · {source.category} · {source.language} · vikt {source.weight}
                  </Typography>
                  {source.notes && <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mt: 0.5 }}>{source.notes}</Typography>}
                </Box>
                <Button size="small" disabled={busyAction === 'import'} sx={{ flexShrink: 0 }} onClick={() => void handleImportRecommended([source.key])}>
                  Importera
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}
